/* Importing the necessary libraries and files */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';

/* Importing the components from the material-ui library. */
import { Box, Card, CardContent, Grid, IconButton, MenuItem, Stack, Tab, TextField, Typography } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';

/* Importing components from the components folder. */
import AutoCompleteLocation from '../../components/AutoCompleteLocation';
import AutoCompletePatient from '../../components/AutoCompletePatient';
import FormHelpDialog from '../../components/FormHelpDialog';
import TelepresenceConnection from '../../components/Telepresence/Connection';

/* Importing the functions from the Redux slices. */
import { getDevice } from '../../features/device/deviceSlice';

/* Importing the functions from the utils folder. */
import fetchAndHandleData from '../../utils/fetchAndHandleData';
import { fDevice } from '../../utils/Device/Device.utils.format';

/* Importing constants from DeviceValueSets.js file. */
import { DEVICE_NAME_TYPE, STATUS, STATUS_REASON } from '../../utils/Device/Device.utils.ValueSets';

DeviceSectionDetails.propTypes = {
  deviceID: PropTypes.string,
  call: PropTypes.bool,
};

/**
 * Renders the details section of a device.
 * @param {Object} props - The component props.
 * @param {string} props.deviceID - The ID of the device to display details for.
 * @returns {JSX.Element} - The rendered component.
 */
export default function DeviceSectionDetails(props) {
  const { deviceID, call } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { Devices } = useSelector((state) => state.device);
  const currentDevice = Devices.find((el) => el.id === deviceID);

  const [value, setValue] = useState('1');
  const [pageDevice, setPageDevice] = useState({
    status: '',
    statusReason: '',
    manufacturer: '',
    serialNumber: '',
    deviceName_name: '',
    deviceName_type: '',
    patient: '',
    location: '',
    url: '',
    type: '',
  });

  const [formHelpOpen, setFormHelpOpen] = useState(false);
  const [valueSet, setValueSet] = useState(null);

  const handleFormHelpOpen = (valueSet) => {
    setValueSet(valueSet);
    setFormHelpOpen(true);
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (newValue !== '1') {
      setSearchParams({ tab: newValue });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    if (!currentDevice) return;
    setPageDevice(fDevice(currentDevice));
  }, [currentDevice]);

  const initializeData = useCallback(async () => {
    await fetchAndHandleData({
      dispatch,
      navigate,
      fetchAction: getDevice,
      id: deviceID,
      errorMessage: 'No Device record found',
      navigateOnFailure: true,
      navigateTo: '/device/list',
    });

    if (searchParams.get('tab')) {
      setValue(searchParams.get('tab'));
    }
  }, [deviceID, dispatch, navigate, searchParams]);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ width: '100%', typography: 'body1' }}>
              <TabContext value={value}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <TabList onChange={handleChange} aria-label="lab API tabs example">
                    <Tab label="Device Information" value="1" />
                    {pageDevice.type === 'myBESHIE' && <Tab label="Telepresence" value="telepresence" />}
                  </TabList>
                </Box>
                <TabPanel value="1">
                  <Grid container item xs={12} columnSpacing={4} rowSpacing={3}>
                    <Grid item xs={12} md={12}>
                      <Stack spacing={3}>
                        <Stack spacing={2}>
                          <Typography variant="subtitle1" sx={{ pt: 1 }}>
                            Device Identification
                          </Typography>
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            fullWidth
                            label="Device Name"
                            value={pageDevice.deviceName_name || ''}
                            style={{ border: 'none' }}
                            variant="standard"
                            InputProps={{
                              readOnly: true,
                            }}
                          />
                          <TextField
                            fullWidth
                            select
                            label="Device Name's Type"
                            value={pageDevice.deviceName_type || ''}
                            style={{ border: 'none' }}
                            variant="standard"
                            InputProps={{
                              readOnly: true,
                            }}
                          >
                            {DEVICE_NAME_TYPE.values.map((el) => (
                              <MenuItem key={el.code} value={el.code}>
                                {el.display}
                              </MenuItem>
                            ))}
                          </TextField>
                          <IconButton
                            aria-label="help"
                            onClick={() => {
                              handleFormHelpOpen(DEVICE_NAME_TYPE);
                            }}
                          >
                            <HelpOutlineIcon />
                          </IconButton>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            fullWidth
                            label="Serial Number"
                            value={pageDevice.serialNumber || ''}
                            style={{ border: 'none' }}
                            variant="standard"
                            InputProps={{
                              readOnly: true,
                            }}
                          />
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            fullWidth
                            label="Device Type"
                            value={pageDevice.type || ''}
                            style={{ border: 'none' }}
                            variant="standard"
                            InputProps={{
                              readOnly: true,
                            }}
                          />
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            fullWidth
                            label="Manufacturer"
                            value={pageDevice.manufacturer || ''}
                            style={{ border: 'none' }}
                            variant="standard"
                            InputProps={{
                              readOnly: true,
                            }}
                          />
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            fullWidth
                            label="URL"
                            value={pageDevice.url || ''}
                            style={{ border: 'none' }}
                            variant="standard"
                            InputProps={{
                              readOnly: true,
                            }}
                          />
                        </Stack>

                        <Stack spacing={2}>
                          <Typography variant="subtitle1" sx={{ pt: 1 }}>
                            Device Status
                          </Typography>
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <TextField
                            fullWidth
                            select
                            label="Status"
                            value={pageDevice.status || ''}
                            style={{ border: 'none' }}
                            variant="standard"
                            InputProps={{
                              readOnly: true,
                            }}
                          >
                            {STATUS.values.map((el) => (
                              <MenuItem key={el.code} value={el.code}>
                                {el.display}
                              </MenuItem>
                            ))}
                          </TextField>
                          <IconButton
                            aria-label="help"
                            onClick={() => {
                              handleFormHelpOpen(STATUS);
                            }}
                          >
                            <HelpOutlineIcon />
                          </IconButton>

                          <TextField
                            fullWidth
                            select
                            label="Status Reason"
                            value={pageDevice.statusReason || ''}
                            style={{ border: 'none' }}
                            variant="standard"
                            InputProps={{
                              readOnly: true,
                            }}
                          >
                            {STATUS_REASON.values.map((el) => (
                              <MenuItem key={el.code} value={el.code}>
                                {el.display}
                              </MenuItem>
                            ))}
                          </TextField>
                          <IconButton
                            aria-label="help"
                            onClick={() => {
                              handleFormHelpOpen(STATUS_REASON);
                            }}
                          >
                            <HelpOutlineIcon />
                          </IconButton>
                        </Stack>

                        <Stack spacing={2}>
                          <Typography variant="subtitle1" sx={{ pt: 1 }}>
                            Associated Patient
                          </Typography>
                        </Stack>

                        <Stack spacing={2}>
                          <AutoCompletePatient
                            readOnly
                            fieldName="patient"
                            values={pageDevice}
                            patientID={pageDevice?.patient}
                          />
                        </Stack>

                        <Stack spacing={2}>
                          <Typography variant="subtitle1" sx={{ pt: 1 }}>
                            Device Location
                          </Typography>
                        </Stack>

                        <Stack spacing={2}>
                          <AutoCompleteLocation
                            readOnly
                            fieldName="location"
                            values={pageDevice}
                            locationID={pageDevice?.location}
                          />
                        </Stack>
                      </Stack>
                    </Grid>
                  </Grid>
                </TabPanel>
                {pageDevice.type === 'myBESHIE' && (
                  <TabPanel value="telepresence">
                    <TelepresenceConnection device={pageDevice} setDevice={setPageDevice} call={call}/>
                  </TabPanel>
                )}
              </TabContext>
            </Box>
          </CardContent>
        </Card>
        <FormHelpDialog isOpen={formHelpOpen} setIsOpen={setFormHelpOpen} valueSet={valueSet} />
      </Grid>
    </Grid>
  );
}
